import Phaser from 'phaser';
import { CONFIG, WEAPONS, PASSIVES, ENEMY_TYPES, BOSS_TYPES } from '../data.js';
import { Save } from '../save.js';
import { Player, Enemy, Bullet, Missile, Drone, makePickup } from '../entities.js';

export class GameScene extends Phaser.Scene {
  constructor() { super('Game'); }

  create() {
    const W = this.scale.width;
    const H = this.scale.height;
    this.runStartedAt = this.time.now;
    this.runTime = 0;
    this.difficulty = 1;
    this.kills = 0;
    this.runCredits = 0;
    this.bossesKilled = 0;
    this.nextBossAt = 75;

    // background starfield
    this.starsFar = this.add.group();
    this.starsMid = this.add.group();
    for (let i = 0; i < 60; i++) {
      const s = this.add.image(Math.random() * W, Math.random() * H, 'star_xs');
      s.setAlpha(0.35 + Math.random() * 0.4);
      s._speed = 12 + Math.random() * 18;
      this.starsFar.add(s);
    }
    for (let i = 0; i < 30; i++) {
      const s = this.add.image(Math.random() * W, Math.random() * H, 'star_s');
      s.setAlpha(0.5 + Math.random() * 0.4);
      s._speed = 30 + Math.random() * 40;
      this.starsMid.add(s);
    }

    // physics groups
    this.bullets = this.physics.add.group({
      classType: Bullet,
      maxSize: 800,
      runChildUpdate: false,
    });
    this.missiles = this.physics.add.group({
      classType: Missile,
      maxSize: 60,
      runChildUpdate: false,
    });
    this.enemies = this.physics.add.group({ maxSize: 600 });
    this.pickups = this.physics.add.group();
    this.fxGroup = this.add.group();

    // player + meta upgrades
    this.save = Save.load();
    this.player = new Player(this, W / 2, CONFIG.playerY);
    this.player.applyMetaUpgrades(this.save.meta);
    this.player.grantStartingWeapons();
    this.drones = [];
    this.syncDrones();

    // input
    this.cursors = this.input.keyboard.createCursorKeys();
    this.keys = this.input.keyboard.addKeys('W,A,S,D,P,ESC');
    this.input.keyboard.on('keydown-P', () => this.togglePause());
    this.input.keyboard.on('keydown-ESC', () => this.togglePause());

    // Touch drag-to-move: anchor at (finger pos, ship pos) on press, then
    // track finger delta. Skips touches on the HUD strip so the pause
    // button + HUD region can't double as a movement anchor.
    this._touchAnchor = null;
    this.input.on('pointerdown', (pointer) => {
      if (this.paused || !this.player.active) return;
      if (pointer.y < 58) return; // HUD strip is 56px tall — leave a 2px buffer
      this._touchAnchor = {
        px: pointer.x, py: pointer.y,
        sx: this.player.x, sy: this.player.y,
      };
    });
    const clearAnchor = () => { this._touchAnchor = null; };
    this.input.on('pointerup', clearAnchor);
    this.input.on('pointerupoutside', clearAnchor);
    this.input.on('gameout', clearAnchor);

    // collision handlers
    this.physics.add.overlap(this.bullets, this.enemies, this.onBulletEnemy, null, this);
    this.physics.add.overlap(this.missiles, this.enemies, this.onMissileEnemy, null, this);
    this.physics.add.overlap(this.player, this.enemies, this.onPlayerEnemy, null, this);
    this.physics.add.overlap(this.player, this.pickups, this.onPlayerPickup, null, this);

    if (!this.scene.isActive('HUD')) this.scene.launch('HUD', { game: this });

    this.spawnAccum = 0;
    this.groupSpawnAccum = 0;
    this.paused = false;

    this.events.on('shutdown', () => {
      if (this.scene.isActive('HUD')) this.scene.stop('HUD');
    });
  }

  // ============================================================
  // UPDATE
  // ============================================================
  update(time, delta) {
    if (this.paused || !this.player.active) return;

    this.runTime = (time - this.runStartedAt) / 1000;
    this.difficulty = 1 + this.runTime / 20 + Math.pow(this.runTime / 60, 1.3);

    this.updateStars(delta);
    this.updatePlayerInput(delta);
    this.player.preUpdateTick(delta);
    this.updateWeapons(time, delta);
    this.updateEnemies(time, delta);
    this.updateMissiles(delta);
    this.updateBullets();
    this.updatePickups(delta);
    this.updateDrones(time);
    this.updateSpawner(delta);
    this.updateBosses();
  }

  togglePause() {
    if (this.paused) {
      this.paused = false;
      this.physics.resume();
    } else {
      this.paused = true;
      this.physics.pause();
    }
    this._touchAnchor = null;
  }

  // ============================================================
  // INPUT / MOVEMENT
  // ============================================================
  updatePlayerInput() {
    const p = this.player;
    const speed = p.moveSpeed;
    let vx = 0, vy = 0;
    if (this.cursors.left.isDown || this.keys.A.isDown) vx -= 1;
    if (this.cursors.right.isDown || this.keys.D.isDown) vx += 1;
    if (this.cursors.up.isDown || this.keys.W.isDown) vy -= 1;
    if (this.cursors.down.isDown || this.keys.S.isDown) vy += 1;

    if (vx !== 0 || vy !== 0) {
      // keyboard wins when any direction is held
      if (vx !== 0 && vy !== 0) { vx *= 0.7071; vy *= 0.7071; }
      p.body.setVelocity(vx * speed, vy * speed);
    } else if (this._touchAnchor && this.input.activePointer.isDown) {
      // touch drag: aim for ship-pos-at-anchor + finger-delta, capped at moveSpeed
      const pointer = this.input.activePointer;
      const targetX = this._touchAnchor.sx + (pointer.x - this._touchAnchor.px);
      const targetY = this._touchAnchor.sy + (pointer.y - this._touchAnchor.py);
      const dx = targetX - p.x;
      const dy = targetY - p.y;
      const dist = Math.hypot(dx, dy);
      if (dist < 2) {
        p.body.setVelocity(0, 0);
      } else {
        const ease = Math.min(1, dist / 12); // soft brake when near target
        const ang = Math.atan2(dy, dx);
        p.body.setVelocity(Math.cos(ang) * speed * ease, Math.sin(ang) * speed * ease);
      }
    } else {
      p.body.setVelocity(0, 0);
    }

    if (p.y < CONFIG.playerMoveBandTop) {
      p.y = CONFIG.playerMoveBandTop;
      if (p.body.velocity.y < 0) p.body.setVelocityY(0);
    }
  }

  // ============================================================
  // STARS
  // ============================================================
  updateStars(delta) {
    const dt = delta / 1000;
    const H = this.scale.height;
    const apply = (group) => {
      group.children.iterate((s) => {
        if (!s) return;
        s.y += s._speed * dt;
        if (s.y > H + 4) { s.y = -4; s.x = Math.random() * this.scale.width; }
      });
    };
    apply(this.starsFar);
    apply(this.starsMid);
  }

  // ============================================================
  // WEAPONS
  // ============================================================
  updateWeapons(time, delta) {
    const p = this.player;
    for (const key in p.weaponLevels) {
      const lvl = p.weaponLevels[key];
      const def = WEAPONS[key];
      if (!def) continue;
      p.weaponCooldowns[key] -= delta;
      if (p.weaponCooldowns[key] <= 0) {
        this.fireWeapon(key, lvl, def);
        const baseCd = def.cooldown ? def.cooldown(lvl) : 999999;
        p.weaponCooldowns[key] = baseCd / p.fireRateMult;
      }
    }
  }

  fireWeapon(key, lvl, def) {
    switch (key) {
      case 'blaster': return this.fireBlaster(lvl, def);
      case 'spread':  return this.fireSpread(lvl, def);
      case 'missile': return this.fireMissile(lvl, def);
      case 'shockwave': return this.fireShockwave(lvl, def);
      case 'laser': return this.fireLaser(lvl, def);
      case 'drone': return;
    }
  }

  rollDamage(base) {
    let dmg = base * this.player.damageMult;
    if (Math.random() < this.player.critChance) {
      dmg *= this.player.critMult;
    }
    return dmg;
  }

  fireBlaster(lvl, def) {
    const count = def.count(lvl);
    const pierce = def.pierce(lvl);
    const base = def.damage(lvl);
    const speed = 480 * this.player.projectileSpeedMult;
    const spacing = 10;
    const totalW = (count - 1) * spacing;
    for (let i = 0; i < count; i++) {
      const b = this.bullets.get(this.player.x - totalW / 2 + i * spacing, this.player.y - 14);
      if (!b) return;
      b.setTexture('bullet');
      b.setTint(0xffffff);
      b.fire(0, -speed, this.rollDamage(base), pierce);
    }
  }

  fireSpread(lvl, def) {
    const count = def.count(lvl);
    const base = def.damage(lvl);
    const arc = def.arc(lvl);
    const speed = 420 * this.player.projectileSpeedMult;
    const span = Math.PI * 0.55 * arc;
    for (let i = 0; i < count; i++) {
      const t = count === 1 ? 0.5 : i / (count - 1);
      const a = -Math.PI / 2 - span / 2 + span * t;
      const b = this.bullets.get(this.player.x, this.player.y - 10);
      if (!b) return;
      b.setTexture('bullet');
      b.setTint(0xffaa55);
      b.fire(Math.cos(a) * speed, Math.sin(a) * speed, this.rollDamage(base), 0);
    }
  }

  fireMissile(lvl, def) {
    const count = def.count(lvl);
    const base = def.damage(lvl);
    const blast = def.blastRadius(lvl);
    for (let i = 0; i < count; i++) {
      const m = this.missiles.get(this.player.x + (Math.random() - 0.5) * 20, this.player.y - 8);
      if (!m) return;
      m.blast = blast;
      const a = -Math.PI / 2 + (Math.random() - 0.5) * 0.4;
      m.fire(a, this.rollDamage(base), blast);
    }
  }

  fireShockwave(lvl, def) {
    const r = def.radius(lvl);
    const dmg = this.rollDamage(def.damage(lvl));
    this.enemies.children.iterate((e) => {
      if (!e || !e.active) return;
      if (Phaser.Math.Distance.Between(this.player.x, this.player.y, e.x, e.y) <= r) {
        if (e.hit(dmg)) this.killEnemy(e);
      }
    });
    const ring = this.add.circle(this.player.x, this.player.y, 10, 0x88ffaa, 0.35);
    ring.setStrokeStyle(2, 0x88ffaa, 1);
    ring.setDepth(6);
    this.tweens.add({
      targets: ring, radius: r, alpha: 0, duration: 380,
      onUpdate: (tween, t) => { t.setRadius(t.radius); },
      onComplete: () => ring.destroy(),
    });
  }

  fireLaser(lvl, def) {
    const w = def.width(lvl);
    const dmgPerTick = this.rollDamage(def.damage(lvl));
    const x = this.player.x;
    const beam = this.add.rectangle(x, this.player.y - 12 - 360, w, 720, 0xff66ee, 0.35);
    beam.setStrokeStyle(2, 0xffaaff, 1);
    beam.setDepth(7);
    const life = 220;
    const ev = this.time.addEvent({
      delay: 30, repeat: Math.floor(life / 30) - 1,
      callback: () => {
        beam.x = this.player.x;
        this.enemies.children.iterate((e) => {
          if (!e || !e.active) return;
          if (Math.abs(e.x - beam.x) <= w / 2 + e.width * 0.3 && e.y < this.player.y) {
            if (e.hit(dmgPerTick)) this.killEnemy(e);
          }
        });
      },
    });
    this.tweens.add({
      targets: beam, alpha: 0, duration: life,
      onComplete: () => { beam.destroy(); ev.remove(); },
    });
  }

  syncDrones() {
    const lvl = this.player.weaponLevels.drone || 0;
    const def = WEAPONS.drone;
    const desired = lvl ? def.count(lvl) : 0;
    while (this.drones.length > desired) {
      const d = this.drones.pop();
      d.destroy();
    }
    while (this.drones.length < desired) {
      const i = this.drones.length;
      const angle = (Math.PI * 2 * i) / desired;
      this.drones.push(new Drone(this, this.player, angle));
    }
    for (let i = 0; i < this.drones.length; i++) {
      const d = this.drones[i];
      d.angleOffset = (Math.PI * 2 * i) / this.drones.length;
      d.damage = def.damage(lvl) * this.player.damageMult;
      d.radius = def.orbitRadius(lvl);
      d.spinSpeed = def.orbitSpeed(lvl);
    }
  }

  updateDrones(time) {
    for (const d of this.drones) {
      d.updateDrone(time);
      this.enemies.children.iterate((e) => {
        if (!e || !e.active) return;
        if (Phaser.Math.Distance.Between(d.x, d.y, e.x, e.y) < e.width / 2 + 8) {
          if (d.canHit(e, time)) {
            if (e.hit(d.damage)) this.killEnemy(e);
          }
        }
      });
    }
  }

  // ============================================================
  // BULLETS / MISSILES bookkeeping
  // ============================================================
  updateBullets() {
    this.bullets.children.iterate((b) => {
      if (!b || !b.active) return;
      if (b.y < -20 || b.y > this.scale.height + 20 || b.x < -20 || b.x > this.scale.width + 20) {
        b.disableBody(true, true);
      }
    });
  }

  updateMissiles(delta) {
    this.missiles.children.iterate((m) => {
      if (!m || !m.active) return;
      m.updateMissile(delta, this.enemies);
    });
  }

  // ============================================================
  // ENEMIES
  // ============================================================
  updateEnemies(time, delta) {
    this.enemies.children.iterate((e) => {
      if (!e || !e.active) return;
      e.updateAI(time, delta, this);
    });
  }

  // ============================================================
  // COLLISIONS
  // ============================================================
  onBulletEnemy(bullet, enemy) {
    if (!bullet.active || !enemy.active) return;
    if (bullet._hitSet.has(enemy)) return;
    bullet._hitSet.add(enemy);
    const dmg = bullet.damage;
    this.spawnHitFx(bullet.x, bullet.y);
    if (enemy.hit(dmg)) this.killEnemy(enemy);
    if (bullet._hitSet.size > bullet.pierce) {
      bullet.disableBody(true, true);
    }
  }

  onMissileEnemy(missile, enemy) {
    if (!missile.active || !enemy.active) return;
    const r = missile.blast;
    this.spawnExplosion(missile.x, missile.y, r);
    missile.disableBody(true, true);
    const dmg = missile.damage;
    this.enemies.children.iterate((e) => {
      if (!e || !e.active) return;
      if (Phaser.Math.Distance.Between(missile.x, missile.y, e.x, e.y) <= r) {
        if (e.hit(dmg)) this.killEnemy(e);
      }
    });
  }

  onPlayerEnemy(player, enemy) {
    if (!enemy.active) return;
    const result = player.takeDamage(enemy.damage);
    if (result) {
      this.cameras.main.shake(80, 0.006);
      enemy.y -= 10;
      if (enemy.maxHp <= 3) {
        if (enemy.hit(999)) this.killEnemy(enemy);
      }
    }
    if (player.hp <= 0 && result !== 'revived') this.endRun();
  }

  onPlayerPickup(player, p) {
    if (!p.active) return;
    if (p.kind === 'gem') {
      const leveled = player.addXp(p.value);
      this.spawnFloatText('+' + Math.round(p.value * player.xpMult), p.x, p.y, 0xeeff88);
      if (leveled) this.queueLevelUps();
    } else if (p.kind === 'credit') {
      this.runCredits += p.value;
      this.spawnFloatText('+' + p.value + 'c', p.x, p.y, 0xffd755);
    } else if (p.kind === 'heal') {
      player.heal(1);
      this.spawnFloatText('+HP', p.x, p.y, 0xff7799);
    } else if (p.kind === 'magnet') {
      this.pickups.children.iterate((pp) => {
        if (pp && pp.active && pp.kind === 'gem') pp.magnetized = true;
      });
      this.spawnFloatText('MAGNET', p.x, p.y, 0xff88ff);
    }
    p.destroy();
  }

  // ============================================================
  // KILL / LOOT
  // ============================================================
  killEnemy(enemy) {
    if (!enemy.active) return;
    this.kills += 1;
    this.spawnExplosion(enemy.x, enemy.y, Math.min(40, enemy.width * 0.6),
      enemy.isBoss ? 0xff66ff : 0xffffff);
    const dropXp = enemy.xpValue;
    const x = enemy.x, y = enemy.y;
    if (enemy.isBoss) {
      for (let i = 0; i < 12; i++) {
        makePickup(this, x + (Math.random() - 0.5) * 80, y + (Math.random() - 0.5) * 60, 'gem', 5);
      }
      makePickup(this, x, y, 'gem', 12);
      for (let i = 0; i < Math.ceil(enemy.creditValue); i++) {
        makePickup(this, x + (Math.random() - 0.5) * 60, y + (Math.random() - 0.5) * 40, 'credit', 1);
      }
      makePickup(this, x, y + 10, 'heal', 1);
      makePickup(this, x + 16, y, 'magnet', 1);
      this.cameras.main.shake(220, 0.012);
      this.bossesKilled += 1;
    } else {
      makePickup(this, x, y, 'gem', dropXp);
      if (Math.random() < enemy.creditValue) {
        makePickup(this, x + (Math.random() - 0.5) * 10, y, 'credit', 1);
      }
      if (Math.random() < 0.01) {
        makePickup(this, x, y, 'heal', 1);
      }
      if (Math.random() < 0.005) {
        makePickup(this, x, y, 'magnet', 1);
      }
    }
    enemy.destroy();
  }

  // ============================================================
  // PICKUPS
  // ============================================================
  updatePickups(delta) {
    const px = this.player.x, py = this.player.y;
    const rad = this.player.pickupRadius;
    const rad2 = rad * rad;
    this.pickups.children.iterate((p) => {
      if (!p || !p.active) return;
      const age = this.time.now - p._spawnTime;
      if (age > 80 && !p.magnetized) {
        const d2 = (p.x - px) * (p.x - px) + (p.y - py) * (p.y - py);
        if (d2 < rad2) p.magnetized = true;
      }
      if (p.magnetized) {
        const a = Math.atan2(py - p.y, px - p.x);
        const sp = Math.min(420, 220 + age * 0.25);
        p.body.setVelocity(Math.cos(a) * sp, Math.sin(a) * sp);
      }
    });
  }

  // ============================================================
  // SPAWNER + DIFFICULTY
  // ============================================================
  updateSpawner(delta) {
    const t = this.runTime;
    const interval = Math.max(60, 900 - t * 7);
    this.spawnAccum += delta;
    while (this.spawnAccum >= interval) {
      this.spawnAccum -= interval;
      this.spawnRandomEnemy();
    }
    this.groupSpawnAccum += delta;
    const groupInterval = Math.max(2500, 9000 - t * 60);
    if (this.groupSpawnAccum >= groupInterval) {
      this.groupSpawnAccum = 0;
      this.spawnFormation();
    }
  }

  pickEnemyType() {
    const t = this.runTime;
    const pool = [];
    pool.push({ k: 'grunt', w: 4 });
    pool.push({ k: 'swarm', w: 3 });
    if (t > 12) pool.push({ k: 'diver', w: 2 });
    if (t > 25) pool.push({ k: 'shooter', w: 2 });
    if (t > 40) pool.push({ k: 'drifter', w: 2 });
    if (t > 60) pool.push({ k: 'tank', w: 1.5 });
    if (t > 90) {
      pool.forEach(p => { if (p.k === 'tank' || p.k === 'shooter') p.w += 1; });
    }
    let total = 0; pool.forEach(p => total += p.w);
    let r = Math.random() * total;
    for (const p of pool) {
      r -= p.w;
      if (r <= 0) return p.k;
    }
    return 'grunt';
  }

  spawnRandomEnemy() {
    const W = this.scale.width;
    const x = 20 + Math.random() * (W - 40);
    const k = this.pickEnemyType();
    this.spawnEnemyAt(k, x, -20);
  }

  spawnEnemyAt(typeKey, x, y) {
    const def = Object.assign({}, ENEMY_TYPES[typeKey]);
    if (!def) return;
    const hpMult = 1 + this.difficulty * 0.18;
    const speedMult = 1 + Math.min(2.5, this.difficulty * 0.04);
    const e = new Enemy(this, x, y, def, hpMult, speedMult);
    this.enemies.add(e);
    return e;
  }

  spawnFormation() {
    const W = this.scale.width;
    const patterns = ['line', 'V', 'column', 'rain', 'arc'];
    const pick = patterns[Math.floor(Math.random() * patterns.length)];
    const typeKey = this.runTime > 30
      ? (Math.random() < 0.5 ? 'diver' : 'shooter')
      : 'grunt';
    if (pick === 'line') {
      const count = 6 + Math.floor(this.difficulty);
      for (let i = 0; i < count; i++) {
        const x = (W / (count + 1)) * (i + 1);
        this.time.delayedCall(i * 60, () => this.spawnEnemyAt(typeKey, x, -20));
      }
    } else if (pick === 'V') {
      const count = 9;
      for (let i = 0; i < count; i++) {
        const k = i - (count - 1) / 2;
        const x = W / 2 + k * 36;
        const yDelay = Math.abs(k) * 90;
        this.time.delayedCall(yDelay, () => this.spawnEnemyAt(typeKey, x, -20));
      }
    } else if (pick === 'column') {
      const x = 40 + Math.random() * (W - 80);
      for (let i = 0; i < 6; i++) {
        this.time.delayedCall(i * 220, () => this.spawnEnemyAt(typeKey, x, -20));
      }
    } else if (pick === 'rain') {
      const count = 14 + Math.floor(this.difficulty * 2);
      for (let i = 0; i < count; i++) {
        this.time.delayedCall(i * 80, () => {
          const x = 20 + Math.random() * (W - 40);
          this.spawnEnemyAt('swarm', x, -20);
        });
      }
    } else if (pick === 'arc') {
      const count = 7;
      for (let i = 0; i < count; i++) {
        const t = i / (count - 1);
        const x = 40 + t * (W - 80);
        const yOffset = -Math.sin(t * Math.PI) * 60;
        this.time.delayedCall(i * 50, () => this.spawnEnemyAt('diver', x, -20 + yOffset));
      }
    }
  }

  updateBosses() {
    if (this.runTime >= this.nextBossAt && !this._bossPending) {
      this._bossPending = true;
      this.spawnBoss();
      this.nextBossAt += Math.max(45, 90 - this.bossesKilled * 5);
      this.time.delayedCall(3000, () => { this._bossPending = false; });
    }
  }

  spawnBoss() {
    const W = this.scale.width;
    const bossKey = (this.bossesKilled % 2 === 0) ? 'mothership' : 'hivemind';
    const tpl = BOSS_TYPES[bossKey];
    const def = Object.assign({}, tpl, { isBoss: true });
    const hpMult = 1 + this.bossesKilled * 0.8 + this.difficulty * 0.4;
    const speedMult = 1 + Math.min(1.5, this.difficulty * 0.03);
    const e = new Enemy(this, W / 2, -60, def, hpMult, speedMult);
    this.enemies.add(e);
    const hud = this.scene.get('HUD');
    if (hud && hud.flashBossWarning) hud.flashBossWarning(tpl.name.toUpperCase());
  }

  // ============================================================
  // FX
  // ============================================================
  spawnHitFx(x, y) {
    const fx = this.add.image(x, y, 'px').setDepth(11).setBlendMode(Phaser.BlendModes.ADD);
    fx.setTint(0xffffff);
    fx.setScale(6, 6);
    this.tweens.add({
      targets: fx, alpha: 0, scale: 0, duration: 120,
      onComplete: () => fx.destroy(),
    });
  }

  spawnExplosion(x, y, r, color) {
    color = color || 0xffaa66;
    const circle = this.add.circle(x, y, 4, color, 0.7).setDepth(11).setBlendMode(Phaser.BlendModes.ADD);
    this.tweens.add({
      targets: circle, radius: r, alpha: 0, duration: 280,
      onUpdate: (tw, t) => { t.setRadius(t.radius); },
      onComplete: () => circle.destroy(),
    });
    const n = Math.min(14, Math.floor(r / 5));
    for (let i = 0; i < n; i++) {
      const ang = Math.random() * Math.PI * 2;
      const sp = 60 + Math.random() * 140;
      const dot = this.add.image(x, y, 'px').setTint(color).setDepth(11).setBlendMode(Phaser.BlendModes.ADD).setScale(2);
      this.tweens.add({
        targets: dot,
        x: x + Math.cos(ang) * sp * 0.4,
        y: y + Math.sin(ang) * sp * 0.4,
        alpha: 0,
        scale: 0,
        duration: 320 + Math.random() * 220,
        onComplete: () => dot.destroy(),
      });
    }
  }

  spawnFloatText(text, x, y, color) {
    const t = this.add.text(x, y, text, {
      fontFamily: 'Courier New, monospace',
      fontSize: '12px',
      color: '#' + color.toString(16).padStart(6, '0'),
      stroke: '#000', strokeThickness: 2,
    }).setOrigin(0.5).setDepth(30);
    this.tweens.add({
      targets: t, y: y - 24, alpha: 0, duration: 700,
      onComplete: () => t.destroy(),
    });
  }

  // ============================================================
  // LEVEL UP
  // ============================================================
  queueLevelUps() {
    this._pendingLevelUps = (this._pendingLevelUps || 0) + 1;
    if (!this._levelUpOpen) this.openLevelUp();
  }

  openLevelUp() {
    this._levelUpOpen = true;
    this.paused = true;
    this.physics.pause();
    this._touchAnchor = null;
    const options = this.rollUpgradeOptions(3);
    this.scene.launch('Upgrade', { game: this, options });
  }

  applyUpgradeAndContinue(choice) {
    this.applyUpgrade(choice);
    this._pendingLevelUps = Math.max(0, (this._pendingLevelUps || 1) - 1);
    if (this._pendingLevelUps > 0) {
      const options = this.rollUpgradeOptions(3);
      this.scene.launch('Upgrade', { game: this, options });
    } else {
      this._levelUpOpen = false;
      this.paused = false;
      this.physics.resume();
      this._touchAnchor = null;
    }
  }

  rollUpgradeOptions(n) {
    const p = this.player;
    const pool = [];
    for (const key in WEAPONS) {
      const def = WEAPONS[key];
      const lvl = p.weaponLevels[key] || 0;
      if (lvl >= def.maxLevel) continue;
      const distinct = Object.keys(p.weaponLevels).length;
      if (lvl === 0 && distinct >= 6) continue;
      pool.push({ kind: 'weapon', key, nextLevel: lvl + 1, weight: lvl === 0 ? 2.5 : 4 });
    }
    for (const key in PASSIVES) {
      const def = PASSIVES[key];
      const lvl = p.passiveLevels[key] || 0;
      if (lvl >= def.maxLevel) continue;
      pool.push({ kind: 'passive', key, nextLevel: lvl + 1, weight: 3 });
    }
    pool.push({ kind: 'heal', key: 'heal', nextLevel: 1, weight: 0.8 });

    const picks = [];
    const used = new Set();
    for (let i = 0; i < n; i++) {
      let total = 0;
      for (const o of pool) if (!used.has(o.key + ':' + o.kind)) total += o.weight;
      if (total <= 0) break;
      let r = Math.random() * total;
      for (const o of pool) {
        if (used.has(o.key + ':' + o.kind)) continue;
        r -= o.weight;
        if (r <= 0) {
          picks.push(o);
          used.add(o.key + ':' + o.kind);
          break;
        }
      }
    }
    return picks;
  }

  applyUpgrade(choice) {
    const p = this.player;
    if (choice.kind === 'weapon') {
      const lvl = (p.weaponLevels[choice.key] || 0) + 1;
      p.weaponLevels[choice.key] = lvl;
      if (p.weaponCooldowns[choice.key] === undefined) p.weaponCooldowns[choice.key] = 0;
      if (choice.key === 'drone') this.syncDrones();
    } else if (choice.kind === 'passive') {
      const def = PASSIVES[choice.key];
      def.apply(p);
      p.passiveLevels[choice.key] = (p.passiveLevels[choice.key] || 0) + 1;
    } else if (choice.kind === 'heal') {
      p.heal(Math.ceil(p.maxHp * 0.5));
    }
  }

  // ============================================================
  // END OF RUN
  // ============================================================
  endRun() {
    if (this._ending) return;
    this._ending = true;
    this.player.setActive(false).setVisible(false);
    this.cameras.main.shake(300, 0.02);
    this.spawnExplosion(this.player.x, this.player.y, 70, 0x66ddff);
    const earnedCredits = Math.floor((this.runCredits + this.kills * 0.05 + this.runTime * 0.1)
      * (this.player.creditMult));
    this.save.credits += earnedCredits;
    this.save.totalKills += this.kills;
    this.save.runs += 1;
    if (this.runTime > this.save.highTime) this.save.highTime = this.runTime;
    if (earnedCredits > this.save.bestCredits) this.save.bestCredits = earnedCredits;
    Save.write(this.save);
    this.time.delayedCall(800, () => {
      this.scene.stop('HUD');
      this.scene.start('GameOver', {
        time: this.runTime,
        kills: this.kills,
        earnedCredits,
        level: this.player.level,
      });
    });
  }
}
